import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Instagram, Play } from "lucide-react";
import { API } from "../App";

// Homepage strip that surfaces Saturn Styling's 6 most-recent Instagram
// posts. Data comes from GET /api/instagram/feed which server-side caches
// the Graph API response for 3h. If the endpoint returns no items (network
// error, unconfigured, or an empty account) the whole section hides itself
// so we never render a broken widget.
const InstagramFeedSection = () => {
  const [items, setItems] = useState(null); // null = loading, [] = hidden

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/instagram/feed`)
      .then((res) => {
        if (!cancelled) setItems(res.data?.items || []);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!items || items.length === 0) return null;

  return (
    <section
      id="instagram"
      className="py-20 md:py-28 relative overflow-hidden"
      data-testid="instagram-feed-section"
    >
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10"
        >
          <div>
            <div className="flex items-center gap-2 text-saturn-orange text-sm uppercase tracking-widest mb-3">
              <Instagram className="w-4 h-4" /> Latest Work
            </div>
            <h2 className="font-orbitron text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight uppercase text-white">
              From the Bay to <span className="text-saturn-orange">Instagram</span>
            </h2>
            <p className="text-slate-400 mt-3 max-w-2xl font-inter">
              Fresh tint jobs, wraps, and installs — straight from our workshop feed.
            </p>
          </div>
          <a
            href="https://www.instagram.com/saturnstyling"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-saturn-orange hover:text-saturn-orange-hover transition-colors font-rajdhani font-semibold whitespace-nowrap"
            data-testid="instagram-follow-link"
          >
            Follow @saturnstyling
            <Instagram className="w-4 h-4" />
          </a>
        </motion.div>

        <div
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4"
          data-testid="instagram-grid"
        >
          {items.map((item, idx) => (
            <InstagramTile key={item.id} item={item} index={idx} />
          ))}
        </div>
      </div>
    </section>
  );
};

// One grid tile. Photos render as an <img>. Videos/Reels render as their
// thumbnail with a play overlay — clicking the overlay toggles to an
// inline <video> element (with controls, no autoplay-with-sound since
// browsers block that anyway). Clicking outside the play button opens
// the actual Instagram permalink in a new tab, matching typical
// "click-through to full post" behaviour of embedded feeds.
const InstagramTile = ({ item, index }) => {
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef(null);
  const isVideo = item.media_type === "VIDEO" || item.media_product_type === "REELS";
  const isReel = item.media_product_type === "REELS";

  const handlePlayClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setPlaying(true);
    // Play the video on next tick once the element is mounted.
    requestAnimationFrame(() => {
      const v = videoRef.current;
      if (v) {
        v.muted = false;
        v.play().catch(() => {
          // Autoplay with audio blocked → retry muted so at least visuals play.
          v.muted = true;
          v.play().catch(() => {});
        });
      }
    });
  };

  const altText =
    (item.caption && item.caption.slice(0, 120)) || "Saturn Styling Instagram post";

  const commonWrapper = (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.05 }}
      className="relative group aspect-square overflow-hidden rounded-xl bg-slate-900 border border-slate-800/60"
      data-testid={`instagram-tile-${index}`}
    >
      {playing && isVideo && item.media_url ? (
        <video
          ref={videoRef}
          src={item.media_url}
          controls
          playsInline
          className="w-full h-full object-cover"
          data-testid={`instagram-video-${index}`}
        />
      ) : (
        <>
          <img
            src={item.preview_url}
            alt={altText}
            loading="lazy"
            referrerPolicy="no-referrer"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          {/* Orange bottom gradient — theme match */}
          <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-saturn-black/80 via-saturn-black/20 to-transparent pointer-events-none" />

          {/* Reel / Video badge */}
          {isVideo && (
            <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 rounded-full bg-black/60 backdrop-blur-sm text-white text-[10px] uppercase tracking-wider font-rajdhani font-semibold">
              <Play className="w-3 h-3" fill="currentColor" />
              {isReel ? "Reel" : "Video"}
            </div>
          )}

          {/* Play button overlay for videos */}
          {isVideo && item.media_url && (
            <button
              type="button"
              onClick={handlePlayClick}
              aria-label="Play video"
              data-testid={`instagram-play-${index}`}
              className="absolute inset-0 flex items-center justify-center opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100 transition-opacity bg-black/30"
            >
              <span className="w-14 h-14 rounded-full bg-saturn-orange/90 hover:bg-saturn-orange text-white flex items-center justify-center shadow-lg">
                <Play className="w-6 h-6 ml-1" fill="currentColor" />
              </span>
            </button>
          )}
        </>
      )}
    </motion.div>
  );

  // Wrap the tile in an <a> so any click outside the play button opens
  // the Instagram permalink. When playing inline we drop the link so
  // video controls can be used without navigating away.
  if (playing) return commonWrapper;
  return (
    <a
      href={item.permalink}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`instagram-link-${index}`}
      className="block"
    >
      {commonWrapper}
    </a>
  );
};

export default InstagramFeedSection;
